import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {gsApi} from "../auth/auth";
import {
  organizations,
  deleteThunkResponse,
  getThunkResponse,
  createThunkResponse,
  thunkResponse, tournamentManagers,
} from "../app/api";
import {
  getOpts,
  getResponse,
  getSerializedData,
  getIDRelationship,
  mediumDelay,
  extraLongDelay,
} from "../helpers/JSONapi";

// Fetch
export const getOrganizationUsers = createAsyncThunk(
  "users/getOrganizationUsers",
  async (id, thunkAPI) => {
    const response = await gsApi.get(
      `${organizations}/${id}/organization-users`
    );
    return response;
  }
);

export const getOrganizationAccessList = createAsyncThunk(
  "users/getOrganizationAccessList",
  async (id, thunkAPI) => {
    const response = await gsApi.get(
      `${organizations}/${id}/access-control-list`
    );
    return response;
  }
);

export const getTournamentManager = createAsyncThunk(
  "users/getTournamentManager",
  async (id, thunkAPI) => {
    const response = await gsApi.get(`${tournamentManagers}/${id}`);
    return response;
  }
);

// delete
export const deleteTournamentManager = createAsyncThunk(
  "users/deleteTournamentManager",
  async (id, thunkAPI) => {
    const response = await gsApi.delete(`${tournamentManagers}/${id}`, {
      data: {},
    });
    return response;
  }
);

// add
export const addTournamentManager = createAsyncThunk(
  "users/addTournamentManager",
  async (manager, thunkAPI) => {
    const serializedData = getSerializedData(
      manager,
      "tournament-manager",
      getOpts(manager, [
        ...getIDRelationship("tournament"),
        ...getIDRelationship("organization"),
      ])
    );
    return await getResponse(
      gsApi.post,
      `${tournamentManagers}`,
      serializedData,
      thunkAPI
    );
  }
);

// resend invitation
export const resendManagerInvitation = createAsyncThunk(
  "users/resendManagerInvitation",
  async (id, thunkAPI) => {
    return await getResponse(
      gsApi.post,
      `${tournamentManagers}/${id}/resend-invitation`,
      {},
      thunkAPI
    );
  }
);

export const userSlice = createSlice({
  name: "users",
  initialState: {
    selectedOrganizationMembers: [],
    selectedManager: {},
    notifications: [],
    loading: [],
  },
  reducers: {
    setMember: (state, action) => {
      state.selectedMember = action.payload;
    },
    setManager: (state, action) => {
      state.selectedManager = action.payload;
    },
    clearNotifications: (state) => {
      state.notifications = [];
    },
  },
  extraReducers: {
    // get
    ...getThunkResponse(getOrganizationUsers, (state, action) => {
      const members = action.payload.map?.((member) => ({
        ...member,
        type: member.role === "tournament_organizer" ? "Tournament Organizer" : `Organization ${member.role.charAt(0).toUpperCase() + member.role.slice(1)}`,
      }));
      state.selectedOrganizationMembers = members;
    }),
    ...getThunkResponse(getOrganizationAccessList, (state, action) => {
      const members = [
        ...action.payload?.organizationUsers,
        ...action.payload?.tournamentUsers,
      ]
      state.selectedOrganizationMembers = members;
    }),
    ...getThunkResponse(getTournamentManager, (state, action) => {
      state.selectedManager = action.payload;
    }),
    // delete
    ...deleteThunkResponse(
      deleteTournamentManager,
      (state, action) => {
        const filteredList = state.selectedOrganizationMembers?.filter?.(
          (member) => member.id !== action.meta.arg
        );
        state.selectedOrganizationMembers = filteredList;
      },
      "Tournament Manager"
    ),
    // add
    ...createThunkResponse(
      addTournamentManager,
      (state, action) => {
        state.selectedManager = {
          ...action.payload,
          type: "Tournament Manager",
        };
      },
      "Tournament Manager"
    ),
    // resend invitations
    ...thunkResponse(
      resendManagerInvitation,
      (state, action) => {},
      "Manager Invitation",
      {
        pendingDelay: mediumDelay,
        fulfilledDelay: mediumDelay,
        rejectedDelay: extraLongDelay,
        pendingText: "Sending Invitation",
        fulfilledText: "Invitation Sent",
      }
    ),
  },
});

export const { clearNotifications, setMember, setManager } = userSlice.actions;

export default userSlice.reducer;

export const selectUsersNotifications = (state) => state.users.notifications;

export const selectOrganizationMembers = (state) =>
  state.users.selectedOrganizationMembers;

export const selectManager = (state) => state.users.selectedManager;

export const selectLoading = (state) => state.users.loading;
